/**
 * The login Event Hub acts as — the admin's own, or the user they are viewing as.
 *
 * The pages use this login for what they compute client-side — "is this my event", "which of
 * these nominations are mine". The request header (`X-Event-Hub-Impersonate`, what the SERVER
 * attributes a request to) is derived by EventHubClient from the same inputs through the same
 * decision (utils/eventHubLogin), so the two cannot disagree. If they did, a page would show the
 * admin's ownership over the impersonated user's data.
 *
 * Driven by the portal's View as Other User, not by a control of Event Hub's own. Event Hub
 * used to have one (ImpersonateModal), which set the header independently; with two controls an
 * admin could be "viewing as" one user in the portal and another in Event Hub at once.
 *
 * Two different admin groups are involved, and they are not interchangeable:
 *
 *   rocky-admin       may switch the portal's View as (ImpersonationContext)
 *   event-hub-admin   may send the impersonation header — the service 403s anyone else
 *
 * So a rocky-admin who is not an event-hub-admin is viewing as someone in the portal, but Event
 * Hub cannot follow. Sending the header for them would turn every Event Hub request into a 403,
 * which reads as the module being down. Instead the header is withheld, Event Hub stays on their
 * own login, and `unsupported` lets the layout say so — otherwise the portal's banner would
 * claim a view Event Hub is not actually showing.
 *
 * What this does NOT decide: roles. The service authorizes an impersonated request with the
 * impersonated user's roles (DefaultRequestAuthorizer), which no client-side token carries, so
 * those come from GetCurrentUser — see useEventHubRoles. The `roles` used here are the
 * caller's own, on purpose: whether the header may be sent at all is the admin's right, not the
 * viewed user's.
 *
 * Writes stay enabled, deliberately. Elsewhere in the portal View as is read-only (the chat
 * input locks), but in Event Hub acting on someone's behalf is the point: an admin can do what
 * the viewed user can — create, nominate, approve — and the service records those writes under
 * that user — `createdBy`, `updatedBy`, and the emails approve / reject send. The audit trail keeps
 * the real admin as `realActorId`. Do not "fix" this to match the chat panel.
 */

import { useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useImpersonation } from '../../context/ImpersonationContext';
import { rolesOf } from '../utils/authorization';
import { eventHubLoginFor, type EventHubLogin } from '../utils/eventHubLogin';

export { eventHubLoginFor, type EventHubLogin };

export function useEventHubLogin(): EventHubLogin {
  const { uid, ldap_groups } = useAuth();
  const { targetAlias, isImpersonating } = useImpersonation();
  const roles = useMemo(() => rolesOf(ldap_groups), [ldap_groups]);
  return useMemo(
    () => eventHubLoginFor({ uid, targetAlias, isImpersonating, roles }),
    [uid, targetAlias, isImpersonating, roles],
  );
}
